import { serializeDocumentEvidenceToken } from "../document-evidence-link.js";
import { formatPageLabel } from "../embedded-objects.js";
export const DOCUMENT_READ_MAX_BLOCKS = 40;
export const DOCUMENT_READ_MAX_TOTAL_CHARS = 18_000;
export const DOCUMENT_READ_MAX_BLOCK_CHARS = 4_000;
export const DOCUMENT_TOOL_EVIDENCE_MAX_ITEMS = DOCUMENT_READ_MAX_BLOCKS;
/**
 * From how many blocks a cold, selector-less `document_read` warns that sequential paging is the
 * expensive path. Below three read windows, finishing the read costs at most two more calls,
 * cheaper than an outline detour; at or above it, `document_outline` plus targeted section reads
 * beat blind paging.
 */
export const DOCUMENT_READ_SEQUENTIAL_SCAN_NUDGE_MIN_BLOCKS = 3 * DOCUMENT_READ_MAX_BLOCKS;
export const DOCUMENT_TOOL_EVIDENCE_MAX_EXCERPT_CHARS = 600;
export const DOCUMENT_CITATION_LABEL_MAX_CHARS = 200;
export const DOCUMENT_OUTLINE_MAX_HEADINGS = 48;
export const DOCUMENT_OUTLINE_DEFAULT_DEPTH = 2;
export const DOCUMENT_LIST_MAX_ITEMS = 40;
export const DOCUMENT_CARD_SUMMARY_MAX_CHARS = 1_000;
export const DOCUMENT_CARD_KEY_TERMS_MAX_ITEMS = 20;
export const DOCUMENT_ORIGINAL_MAX_IMAGE_COUNT = 6;
/**
 * The largest PDF `document_read_original` hands the model whole.
 *
 * A request-size knob, not a storage one: the bytes live in Convex file storage and travel into
 * the model request only when it is built, so what this bounds is how much of one request a
 * single PDF may occupy before `document_read`'s parsed text is the better answer.
 */
export const DOCUMENT_ORIGINAL_MAX_PDF_BYTES = 8 * 1024 * 1024;
/**
 * How many extracted figures one `document_outline` result enumerates.
 *
 * Ingest allows up to 80 per revision (60 embedded, plus 20 rendered pages on their own budget
 * where the deployment rasters them), so this cap bites often: 60 entries carrying a
 * 200-character description each would spend well over half the 24 KB envelope on a section the
 * caller may not have asked for. 24 fits beside 48 headings and stays under the payload budget's
 * per-array node maximum, so the list is never silently collapsed — when the cap bites, the
 * result says how many it left out and how to narrow the scope.
 */
export const DOCUMENT_OUTLINE_MAX_FIGURES = 24;
export const DOCUMENT_FIGURE_DESCRIPTION_SNIPPET_MAX_CHARS = 200;
/**
 * How far `document_list` counts a document's figures before reporting a lower bound.
 *
 * The listing walks up to `ARTIFACT_PLACEMENT_SCAN_LIMIT` documents in one query, so an
 * unbounded count per row multiplies into the deployment read ceiling. `document_outline` is the
 * authoritative enumeration; this count only answers "figures, and roughly how many".
 */
export const DOCUMENT_LIST_FIGURE_COUNT_CAP = 20;
/**
 * What `document_read_original` may hand the model as image bytes — per image, and per call.
 *
 * **Quality and request-size** knobs, not storage protection: a tool result persists a media part
 * as a reference to the blob in Convex file storage, so what these bound is how much of one model
 * request a single call's pictures may occupy, and therefore how much detail survives the round
 * trip.
 *
 * 4 MiB per image is above every extracted figure the pipeline keeps (the worker's own per-figure
 * ceiling is 5 MB, and anything larger has a model-sized variant beside it), so an ordinary
 * screenshot, diagram or page render travels at full fidelity. The per-call total is twice that
 * rather than six times it: `DOCUMENT_ORIGINAL_MAX_IMAGE_COUNT` pictures of ordinary size all
 * fit, while a request built from six 4 MiB images would crowd out the conversation around them.
 *
 * Deliberately **not** `MODEL_IMAGE_VARIANT_MAX_BYTES` (192 KB): that number is the browser
 * compression ladder's *target* for a chat upload, and used as a delivery limit it refuses every
 * ordinary screenshot the user can see in the composer. It still governs the upload ladder and
 * `element_get`'s embedded-image budget.
 */
export const DOCUMENT_ORIGINAL_MAX_IMAGE_BYTES = 4 * 1024 * 1024;
export const DOCUMENT_ORIGINAL_MAX_IMAGE_TOTAL_BYTES = 8 * 1024 * 1024;
/**
 * Byte sizes as the model should repeat them to a user: "941 KB", "1 MB". Rounded, not exact —
 * an envelope that says a limit was exceeded is more useful than one that is precise to the byte.
 */
export function formatDocumentOriginalBytes(bytes) {
    if (bytes < 1024)
        return `${Math.max(0, Math.round(bytes))} bytes`;
    if (bytes < 1024 * 1024)
        return `${Math.round(bytes / 1024)} KB`;
    return `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`;
}
export const DOCUMENT_SEARCH_MAX_HITS = 20;
export const DOCUMENT_SEARCH_SNIPPET_CHARS = 320;
export const DOCUMENT_SEARCH_CANDIDATE_FANOUT = 128;
/**
 * Shared by the `document_read` tool schema and the backend handler so the model reads the same
 * rule at both boundaries. `sectionRef` / `blockRange` / `pageRange` are three ways to name where
 * a span starts, so at most one may be given; none is a valid request that reads from the first
 * block, and `cursorBlockId` continues a truncated read.
 */
export const DOCUMENT_READ_SELECTOR_MESSAGE = "Provide at most one span selector. Valid: sectionRef alone, blockRange alone, pageRange alone, cursorBlockId alone to continue a truncated read, no selector at all to read from the start of the document, or cursorBlockId combined with one of the three.";
/**
 * The `document_read` cursor-continuation protocol, stated once.
 *
 * Four surfaces tell the model this same rule — the tool description, the document context line,
 * the mandatory product rules, and the `span_bound_continue_with_cursor` warning on the result
 * itself. When they disagree, the model receives contradictory instructions from the system
 * prompt, the tool schema and the tool result in a single turn, with no way to rank them. Same
 * single-source reason as `DOCUMENT_READ_SELECTOR_MESSAGE` above.
 *
 * Pass `cursor` to name the concrete continuation a live result carries; omit it for prose.
 */
export function documentReadContinuationClause(cursor) {
    const selector = cursor === undefined ? "cursorBlockId" : `cursorBlockId="${cursor}"`;
    return `call document_read again with the same selector plus ${selector}, repeating until truncated is false`;
}
/** Sentence-cased form, for a rule list or a result warning. */
export function documentReadContinuationSentence(cursor) {
    const clause = documentReadContinuationClause(cursor);
    return `${clause.charAt(0).toUpperCase()}${clause.slice(1)}.`;
}
/** The other half of the rule: never ask for content the model is holding a cursor into. */
export const DOCUMENT_CONTENT_NEVER_REQUEST_PASTE = "never ask the user to paste, re-upload, or restate content that lives in a project or attached document";
/**
 * Why a figure the caller asked `document_read_original` for was not delivered. Every drop on the
 * figure path names itself here and travels in `omittedFigures`, so the model can tell "too big
 * to send" from "does not exist" and has a recoverable next step.
 *
 * `over_model_budget_no_variant` is the one that carries a repair: a figure whose original
 * exceeds `DOCUMENT_ORIGINAL_MAX_IMAGE_BYTES` becomes deliverable as soon as the document is
 * re-processed, because processing writes a model-sized variant next to it.
 *
 * `result_size_limit` belongs to whoever assembles the model request from the persisted media
 * references — the tool call itself weighs nothing, since it delivers references rather than
 * bytes. It stays distinct from `media_total_budget_exhausted`, which is decided before the bytes
 * are ever read.
 *
 * This vocabulary covers *figures* only. A standalone uploaded image asked for by artifact id has
 * no block, no reprocess to offer and no figure identity; it reports itself with
 * `status: "too_large"` instead, and naming it here would publish the same refusal twice.
 */
export const DOCUMENT_ORIGINAL_FIGURE_OMISSION_REASONS = [
    "over_model_budget_no_variant",
    "media_total_budget_exhausted",
    "result_size_limit",
    "figure_unavailable",
    "figure_not_found",
    "outside_page_range",
];
const DOCUMENT_ORIGINAL_FIGURE_OMISSION_GUIDANCE = {
    over_model_budget_no_variant: "the extracted image is larger than the per-image model limit and the document holds no model-sized copy of it; re-process the document to generate one, and meanwhile use the figure's description from document_read",
    media_total_budget_exhausted: "this call's total image budget was already spent; request the remaining figures in a follow-up document_read_original call",
    result_size_limit: "the figure's bytes did not fit the model request being built; request fewer figureBlockIds per call",
    figure_unavailable: "the extracted image is missing or unreadable; re-process the document",
    figure_not_found: "no figure was extracted for that block id in this revision; take figure block ids from document_outline, which enumerates every extracted figure, or from a document_read figure block",
    outside_page_range: "the figure lies outside the requested pageRange; repeat the call without pageRange or with one that covers it",
};
export function documentOriginalFigureOmissionGuidance(reason) {
    return DOCUMENT_ORIGINAL_FIGURE_OMISSION_GUIDANCE[reason];
}
/**
 * The one narrative that explains a set of omissions, built from the reasons present.
 *
 * The block ids stay in `omittedFigures` and are deliberately not repeated here — the message
 * carries counts and the recovery, the array carries the identities, so the 24 KB envelope budget
 * pays for each of them once.
 */
export function buildDocumentOriginalOmissionWarning(omissions) {
    if (omissions.length === 0)
        return null;
    const counts = new Map();
    for (const omission of omissions) {
        counts.set(omission.reason, (counts.get(omission.reason) ?? 0) + 1);
    }
    const detail = [...counts.entries()]
        .map(([reason, count]) => `${reason} (${count}): ${documentOriginalFigureOmissionGuidance(reason)}`)
        .join(". ");
    return {
        code: "figures_omitted",
        message: `${omissions.length} requested ${omissions.length === 1 ? "figure was" : "figures were"} not delivered; omittedFigures names each one. ${detail}.`,
    };
}
/**
 * Whether an extracted figure can be handed to the model, stated *before* anyone asks for it.
 *
 * `document_read_original` reports the same three facts after the fact; repeating them on the
 * discovery surfaces (`document_outline` figures, `document_read` figure blocks) is what stops a
 * model spending a media call to learn that a figure cannot travel.
 *
 * - `original` — the extracted blob is within `DOCUMENT_ORIGINAL_MAX_IMAGE_BYTES` and travels
 *   as-is.
 * - `downscaled` — a model-sized variant exists and is what the model will receive. The
 *   `figureArtifactId` is unchanged, so `element_embed_image` still embeds full fidelity.
 * - `needs_reprocess` — the original is over `DOCUMENT_ORIGINAL_MAX_IMAGE_BYTES` and the row
 *   holds no variant. Re-processing the document produces one; nothing the model can retry will.
 *
 * The threshold is the *delivery* cap this tool path enforces, not the ingest ladder's
 * `MODEL_IMAGE_VARIANT_MAX_BYTES` target: a 400 KB figure has no variant and needs none, and
 * calling it `needs_reprocess` would invent a repair for an image that sends.
 */
export const DOCUMENT_FIGURE_DELIVERY_STATES = [
    "original",
    "downscaled",
    "needs_reprocess",
];
/**
 * Resolved from the figure row alone — never from the derived artifact, which content-hash dedupe
 * can share between rows while a variant belongs to exactly one.
 *
 * An unmeasured original (`byteSize` absent) reads as `original` on purpose. The delivery path is
 * optimistic in exactly the same way — it weighs the real blob and omits it with a reason if it
 * turns out to be too big — so a pessimistic hint here would invent a reprocess prompt for a
 * figure that delivers fine.
 */
export function resolveDocumentFigureDelivery(figure) {
    if (figure.hasModelVariant)
        return "downscaled";
    if (figure.byteSize !== undefined && figure.byteSize > DOCUMENT_ORIGINAL_MAX_IMAGE_BYTES) {
        return "needs_reprocess";
    }
    return "original";
}
/** One-sentence trim of a figure description for a discovery surface. */
export function documentFigureDescriptionSnippet(description, maxChars = DOCUMENT_FIGURE_DESCRIPTION_SNIPPET_MAX_CHARS) {
    const compact = description?.replace(/\s+/g, " ").trim();
    if (!compact)
        return undefined;
    if (compact.length <= maxChars)
        return compact;
    return `${compact.slice(0, Math.max(1, maxChars - 1)).trimEnd()}…`;
}
/** Reasons a follow-up `document_read_original` with fewer figures can still satisfy. */
export function isRetryableDocumentOriginalOmission(reason) {
    return reason === "media_total_budget_exhausted" || reason === "result_size_limit";
}
/**
 * Shapes that are raw machine identifiers rather than something a human named: a normalized
 * block id (`nb_<hash>`), or a Convex document id (32 lowercase alphanumerics).
 *
 * These are the single source of truth for "this token is an identifier". The Claudia settle-time
 * leakage tripwire (`assistant/turn/documentCitationLeakage.ts`) reuses them, so the detector and
 * the citation compilers can never disagree about what counts as a leak.
 */
// Lookarounds rather than `\b`: `_` is a word character, so `\bnb_…` silently failed to see the
// id in `scan_nb_9f2c41ab77de_p3.png` — exactly the underscore-glued shape a generated filename
// takes. These guards only stop a match mid-alphanumeric-run.
export const RAW_DOCUMENT_BLOCK_ID_PATTERN = /(?<![0-9a-z])nb_[0-9a-z]{8,}(?![0-9a-z])/gi;
// A Convex document id is a 28-36 character lowercase-alphanumeric run, but so is a long word:
// `organizationaltransformation.pdf` is a legitimate filename, and a length-only test would blank
// it out of every citation. The digit requirement separates them — Convex's id alphabet is
// digit-bearing and a natural-language word is not.
export const CONVEX_ID_LIKE_PATTERN = /(?<![0-9a-z])(?=[0-9a-z]{28,36}(?![0-9a-z]))[0-9a-z]*[0-9][0-9a-z]*/gi;
/**
 * True when a raw identifier appears anywhere in the value — not only when the value *is* one.
 * `upload-k1739abcdefghijklmnopqrstuvwx.pdf` leaks just as much as the bare id does.
 */
export function containsRawDocumentIdentifier(value) {
    if (!value)
        return false;
    return (new RegExp(RAW_DOCUMENT_BLOCK_ID_PATTERN.source, "i").test(value) ||
        new RegExp(CONVEX_ID_LIKE_PATTERN.source, "i").test(value));
}
/**
 * Citation chips, citation panels and evidence dialogs show this string to a human, so an
 * identifier is never an acceptable value for it. Any display name carrying one — an unnamed
 * upload, a filename minted from a storage key, a card title that was never set — falls back to
 * the generic noun rather than leaking the id into user-visible citation text.
 *
 * The fallback is deliberately total rather than a redaction: excising the id from the middle of a
 * name produces mangled copy ("upload-.pdf"), and a name that carries a machine id was never a
 * human name to begin with.
 */
export function toCitationDocumentName(value) {
    const trimmed = value?.trim() ?? "";
    if (!trimmed || containsRawDocumentIdentifier(trimmed)) {
        return "Document";
    }
    return trimmed;
}
/** The workbook half of a citation label, or null when the block is not in one. */
function buildWorkbookCitationLocation(args) {
    if (typeof args.sourceRow === "number" && Number.isFinite(args.sourceRow)) {
        return ` · row ${Math.trunc(args.sourceRow).toLocaleString("en-US")}`;
    }
    const cellRange = args.cellRange ? normalizeCitationLabelPart(args.cellRange) : "";
    return cellRange ? ` · ${cellRange}` : null;
}
function normalizeCitationLabelPart(value) {
    return value.replace(/\s+/g, " ").trim();
}
function truncateCitationLabelBase(value, maxChars) {
    if (value.length <= maxChars)
        return value;
    if (maxChars <= 1)
        return "…".slice(0, maxChars);
    return `${value.slice(0, maxChars - 1).trimEnd()}…`;
}
/**
 * What a reader sees beside a quotation: the document, the section it came from, and where.
 *
 * "Where" is a page for a paged document and a place in a workbook for a spreadsheet, which
 * has no pages: a row of a sampled table reads `prices.xlsx — Pricing · row 213,907` and the
 * card or grid of a region reads `prices.xlsx — Pricing · A1:H500`. The row wins over the
 * range when a block carries both, because a row is the sharper answer to "where is this".
 *
 * The section of a workbook block is its sheet rather than the deepest entry of its heading
 * path: that entry is the region's title on a card and `Row 41 of 2,000` on a row, and the
 * locator beside it already says which row, so the tab is the part that adds anything.
 *
 * A rendered view of a dense sheet says which view beside the sheet (`renderView`) and, where its
 * description runs over several blocks, which of them this is (`descriptionPart`). Both are
 * written by `formatRenderView` and `formatDescriptionPartLabel`, so the citation and the block
 * text that carries the same fact cannot word it differently.
 */
export function buildDocumentCitationLabel(args) {
    const documentName = toCitationDocumentName(normalizeCitationLabelPart(args.documentName));
    const sheetName = args.sheetName ? normalizeCitationLabelPart(args.sheetName) : "";
    const renderView = sheetName && args.renderView ? normalizeCitationLabelPart(args.renderView) : "";
    const heading = (sheetName && renderView ? `${sheetName} — ${renderView}` : sheetName) ||
        [...(args.headingPath ?? [])].reverse().map(normalizeCitationLabelPart).find(Boolean);
    const pageStart = args.pageStart ?? null;
    const pageEnd = args.pageEnd ?? pageStart;
    const part = args.descriptionPart ? ` · ${normalizeCitationLabelPart(args.descriptionPart)}` : "";
    const location = `${buildWorkbookCitationLocation(args) ??
        (pageStart === null ? "" : ` (${formatPageLabel(pageStart, pageEnd, "page")})`)}${part}`;
    const base = `${documentName}${heading ? ` — ${heading}` : ""}`;
    return `${truncateCitationLabelBase(base, Math.max(1, DOCUMENT_CITATION_LABEL_MAX_CHARS - location.length))}${location}`;
}
/**
 * The whole locator of one citable block as a single string the model copies rather than builds.
 *
 * Three ids written out separately are three chances to compose one, and a composed Convex id is
 * well-formed enough to reach the database and name nothing. One token is either copied intact or
 * visibly wrong, so `knowledge_add` takes this in place of the three fields.
 */
export function formatDocumentCiteToken(citation) {
    return `${citation.artifactId}/${citation.processingRevisionId}/${citation.blockId}`;
}
/** The three ids a `cite` token holds, or null when the value is not one this read could emit. */
export function parseDocumentCiteToken(value) {
    const parts = value.trim().split("/");
    if (parts.length !== 3)
        return null;
    const [artifactId, processingRevisionId, blockId] = parts;
    if (!artifactId || !processingRevisionId || !blockId)
        return null;
    return { artifactId, processingRevisionId, blockId };
}
export function blockCitationToEvidenceLocator(citation, span) {
    return {
        kind: "document_block",
        artifactId: citation.artifactId,
        processingRevisionId: citation.processingRevisionId,
        blockId: citation.blockId,
        ...(span ? { span } : {}),
    };
}
function boundedEvidenceSpan(text) {
    const startChar = text.search(/\S/);
    if (startChar < 0)
        return null;
    const boundedEndChar = Math.min(text.length, startChar + DOCUMENT_TOOL_EVIDENCE_MAX_EXCERPT_CHARS);
    const boundedText = text.slice(startChar, boundedEndChar);
    const endChar = boundedEndChar - (boundedText.length - boundedText.trimEnd().length);
    return endChar > startChar ? { startChar, endChar } : null;
}
export function documentReadBlockToBlockEvidence(block) {
    const span = boundedEvidenceSpan(block.text);
    if (!span)
        return null;
    const locator = blockCitationToEvidenceLocator(block, span);
    return {
        referenceKind: "documentEvidence",
        locator,
        referenceMarkdown: serializeDocumentEvidenceToken({
            ...locator,
            label: toCitationDocumentName(block.documentName),
        }),
    };
}
export function documentReadBlockToToolEvidence(block) {
    const span = boundedEvidenceSpan(block.text);
    const blockEvidence = documentReadBlockToBlockEvidence(block);
    if (!span || !blockEvidence)
        return null;
    return {
        ...blockEvidence,
        cite: block.cite,
        documentName: block.documentName,
        citationLabel: block.citationLabel,
        excerpt: block.text.slice(span.startChar, span.endChar),
        pageStart: block.pageStart,
        pageEnd: block.pageEnd,
        headingPath: block.headingPath.slice(0, 8),
    };
}
/**
 * Travels on every evidence-bearing document read result, beside the tokens it governs.
 *
 * A read's citation tokens are the only authority a saved citation can rest on, and the model
 * copies them from the result in front of it, not from the tool description that explained them
 * many turns earlier.
 */
/**
 * How a saved citation names its block, stated identically on every tool that returns one.
 *
 * The rule is *copy*, not compose. Three ids written out separately are three chances to assemble
 * a fourth, and an assembled Convex id is well-formed enough to reach the mutation and resolve to
 * nothing — the citation is then dropped and the statement stands on less than it claimed.
 */
export const DOCUMENT_CITE_COPY_RULE = "To record a statement from a block, pass that block's cite value to knowledge_add exactly as it was returned here: it is the whole locator. Never construct one, and never cite a block no read returned — an id a read did not return is dropped by name and the statement loses that citation.";
export const DOCUMENT_READ_CITATION_GUIDANCE = "Cite from this read now: copy each evidence referenceMarkdown token in this result into anything you write from it exactly as returned, with its startChar and endChar unchanged. To record a statement, pass the same block's cite value to knowledge_add exactly as returned — copy it, never assemble one from ids, and never cite a block this result does not list. Write the elements this document supports before reading the next document.";
